import type { LatLngExpression } from "leaflet";

import { convertImpacts } from "./impacts.js";
import type { CompanyDataResponse } from "./types/company.js";
import type {
	Geofence,
	PlantingDataResponse,
	RawCountryPlantingData,
	RawRegionPlantingData,
	RawSubSite,
} from "./types/planting.js";
import type {
	CleanedCompanyData,
	CleanedPlantingData,
	CountryPlantingData,
	RegionPlantingData,
	SubSite,
} from "./types/processed.js";

/**
 * Cleans the raw company data from the API.
 *
 * @param rawData The raw company data from the API.
 * @returns The cleaned company data.
 */
export function cleanCompanyData(
	rawData: CompanyDataResponse,
): CleanedCompanyData {
	const { data } = rawData;
	const impacts = convertImpacts(data.impacts, data.trees_by_forest_types);
	const treesByForestTypes = data.trees_by_forest_types.map((forestType) => ({
		id: forestType.forest_type_id,
		name: forestType.type,
		ordered: forestType.trees_ordered,
		planted: forestType.trees_planted,
		designated: forestType.trees_designated,
	}));
	const countries = data.countries;
	const trees = {
		ordered: data.trees_ordered,
		planted: data.trees_planted,
		designated: data.trees_designated,
	};
	return {
		impacts,
		treesByForestTypes,
		countries,
		trees,
	};
}

/**
 * Cleans the raw planting data from the API.
 *
 * @param rawData The raw planting data from the API.
 * @returns The cleaned planting data.
 */
export function cleanPlantingData(
	rawData: PlantingDataResponse,
): CleanedPlantingData {
	return rawData.data.map(cleanCountry);
}

/**
 * Cleans the raw country data.
 *
 * @param raw The raw country data.
 * @returns The cleaned country data.
 */
function cleanCountry(raw: RawCountryPlantingData): CountryPlantingData {
	const { id, name, lat, long, regions } = raw;
	return {
		id,
		name,
		lat,
		long,
		regions: regions.map(cleanRegion),
	};
}

/**
 * Cleans the raw region data.
 *
 * @param raw The raw region data.
 * @returns The cleaned region data.
 */
function cleanRegion(raw: RawRegionPlantingData): RegionPlantingData {
	const { id, name, center_lat, center_long, organization, subsites } = raw;
	return {
		id,
		name,
		lat: center_lat,
		long: center_long,
		organization,
		subSites: subsites.map(cleanSubSite),
	};
}

/**
 * Cleans the raw subsite data.
 *
 * @param raw The raw subsite data.
 * @returns The cleaned subsite data.
 */
function cleanSubSite(raw: RawSubSite): SubSite {
	const {
		id,
		name,
		description,
		geofence: rawGeofence,
		site_manager_name,
		site_manager_title,
		type,
		forest_type_name,
		forest_type_id,
		total_planters,
		trees_designated = 0,
		trees_ordered = 0,
		trees_planted = 0,
		kelp_designated = 0,
		kelp_ordered = 0,
		kelp_planted = 0,
		subsite_area,
	} = raw;

	const counts =
		type === "Trees"
			? {
					designated: trees_designated,
					ordered: trees_ordered,
					planted: trees_planted,
				}
			: {
					designated: kelp_designated,
					ordered: kelp_ordered,
					planted: kelp_planted,
				};
	const geofence = rawGeofence
		? {
				type: rawGeofence.type,
				coordinates: convertCoordinates(rawGeofence.coordinates),
			}
		: undefined;
	return {
		id,
		name,
		description,
		geofence,
		manager: {
			name: site_manager_name,
			title: site_manager_title,
		},
		type,
		forestType: {
			id: forest_type_id,
			name: forest_type_name,
		},
		numberOfPlanters: total_planters,
		image: getSubSiteImage(raw),
		area: subsite_area,
		counts,
	};
}

/**
 * Gets the image for a subsite.
 *
 * @param subsite The subsite to get the image for.
 * @returns The image for the subsite or null if undefined.
 */
export function getSubSiteImage(
	subsite: Pick<RawSubSite, "subsite_main_image_url">,
): string | null {
	return subsite.subsite_main_image_url?.cdn_url || null;
}

/**
 * Converts the raw coordinates to the format used by Leaflet.
 *
 * @param raw The raw coordinates.
 * @returns The converted coordinates.
 */
function convertCoordinates(
	raw: Geofence["coordinates"],
): LatLngExpression[][][] {
	return raw.map((polygon) =>
		polygon.map((ring) => ring.map(([lng, lat]) => [lat, lng])),
	);
}
